import type { Room } from '@hollowveil/shared';

/**
 * Le Sanctuaire des Dimensions - Hub Central
 *
 * C'est ici que Côtelette rencontre l'Oracle Chevelu (Benours déguisé)
 * et apprend les bases du jeu (tutoriel Real/Spectral).
 *
 * 5 portails vers les différents mondes (1 actif au départ, les autres se débloquent)
 */
export const roomSanctuary: Room = {
  id: 'room_sanctuary',
  name: 'Le Sanctuaire des Dimensions',
  description: 'Un espace flottant entre les mondes, où l\'Oracle Chevelu attend les élus.',
  size: [20, 8, 20],
  palette: 'mystical',
  floorStyle: 'spatial',
  initialWorld: 'real',
  hasSpectralLayer: true,
  spawnPosition: [3, 0, 3], // Spawn en bas à gauche, face à la pierre
  ambiance: {
    realWorld: {
      backgroundColor: '#1a1a2e',
      ambientLightColor: '#9d8cff',
      ambientLightIntensity: 0.6,
      fogColor: '#1a1a2e',
      fogDensity: 0.02,
    },
    spectralWorld: {
      backgroundColor: '#0d0d1a',
      ambientLightColor: '#6c5ce7',
      ambientLightIntensity: 0.4,
      fogColor: '#0d0d1a',
      fogDensity: 0.03,
    },
  },
  objects: [
    // Plateforme centrale
    {
      id: 'obj_central_platform',
      name: 'Plateforme Centrale',
      type: 'static',
      modelId: 'platform_crystal',
      position: [10, 0, 10],
      rotation: [0, 0, 0],
      scale: [4, 0.5, 4],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    // ============================================
    // MONDE RÉEL - Pierre Ancestrale (tutoriel)
    // ============================================
    // La Pierre Ancestrale - indice pour passer en spectral (près de l'entrée)
    {
      id: 'obj_ancient_stone',
      name: 'Pierre Ancestrale',
      type: 'interactive',
      modelId: 'ancient_stone',
      position: [10, 0, 4],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      collider: { type: 'circle', size: [0.6] }, // Petit collider pour ne pas bloquer trop loin
      onInteract: [
        {
          type: 'showRiddle',
          params: {
            riddle: {
              id: 'riddle_ancient_stone',
              npcId: 'obj_ancient_stone',
              npcName: '📜 Parchemin Ancien',
              question: 'parchment:Voyageuse qui cherche la vérité,\nSache que deux mondes coexistent ici.\n\nLe monde RÉEL que tes yeux voient,\nEt le monde SPECTRAL, caché au-delà du Voile.\n\n✨ Pour révéler le monde spectral :\n\n📱 Sur mobile : appuie sur le bouton SPECTRAL\n💻 Sur ordinateur : appuie sur ESPACE\n\nUn sage t\'attend de l\'autre côté...\nIl a une magnifique chevelure.',
              type: 'multiple_choice',
              choices: [
                { id: 'a', text: 'Je comprends, allons-y !', isCorrect: true },
              ],
              correctResponse: 'Le parchemin s\'illumine... Le monde spectral t\'attend !',
              incorrectResponse: '',
              canRetry: true,
            },
          },
        },
      ],
    },

    // ============================================
    // MONDE SPECTRAL - L'Oracle Chevelu
    // ============================================
    // Coussin de l'Oracle (visible en spectral) - en haut au centre
    {
      id: 'obj_oracle_cushion',
      name: 'Coussin de l\'Oracle',
      type: 'static',
      modelId: 'cushion_gold',
      position: [10, 0.5, 16],
      rotation: [0, 0, 0],
      scale: [1.5, 0.3, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },
    // L'Oracle Chevelu (Benours déguisé) - Visible uniquement en spectral
    // Position: en haut au centre de la pièce, face vers le bas (vers le joueur)
    {
      id: 'obj_oracle',
      name: 'L\'Oracle Chevelu',
      type: 'interactive',
      modelId: 'oracle_chevelu',
      position: [10, 0, 16],
      rotation: [0, Math.PI, 0], // Face vers le bas de la salle
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        isNPC: true,
        interactionRadius: 4,
      },
      collider: { type: 'circle', size: [0.8] }, // Petit collider pour ne pas bloquer
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: {
            riddleType: 'oracle',
          },
        },
      ],
    },
    // ============================================
    // SPHÈRES RUNIQUES - PORTAILS AU SOL
    // ============================================
    // Sphère Wonderland - s'active après avoir parlé à l'Oracle
    {
      id: 'obj_portal_wonderland',
      name: 'Sphère de Wonderland',
      type: 'interactive',
      modelId: 'runic_sphere_portal',
      position: [5, 0, 5],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        portalTo: 'room_wonderland_reveil',
        isActive: false, // Inactif par défaut
        activateAfterRiddleSolved: 'riddle_oracle_welcome', // S'active après l'Oracle
        portalColor: '#ff69b4',
      },
      collider: { type: 'circle', size: [1.0] },
      onInteract: [
        {
          type: 'loadRoom',
          params: { roomId: 'room_wonderland_reveil' },
        },
      ],
    },
    // Sphère Hawkins - s'active après avoir parlé à l'Oracle post-Wonderland
    {
      id: 'obj_portal_hawkins',
      name: 'Sphère de Hawkins',
      type: 'interactive',
      modelId: 'runic_sphere_portal',
      position: [15, 0, 5],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        portalTo: 'room_hawkins_reveil',
        isActive: false,
        activateAfterRiddleSolved: 'riddle_oracle_wonderland_done', // S'active après avoir parlé à l'Oracle
        portalColor: '#ff0000',
      },
      collider: { type: 'circle', size: [1.0] },
      onInteract: [
        {
          type: 'loadRoom',
          params: {
            roomId: 'room_hawkins_reveil',
            blockedMessage: 'Parle à l\'Oracle pour découvrir ta prochaine destination !',
          },
        },
      ],
    },
    // Sphère Jurassic Park - s'active après avoir complété Hawkins
    {
      id: 'obj_portal_jurassic',
      name: 'Sphère de Jurassic Park',
      type: 'interactive',
      modelId: 'runic_sphere_portal',
      position: [5, 0, 15],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        portalTo: 'room_jurassic_reveil',
        isActive: false,
        activateAfterRoomCompleted: 'room_hawkins_reveil', // S'active après Hawkins
        portalColor: '#228b22',
      },
      collider: { type: 'circle', size: [1.0] },
      onInteract: [
        {
          type: 'loadRoom',
          params: {
            roomId: 'room_jurassic_reveil',
            blockedMessage: 'Cette sphère s\'activera quand tu auras aidé Vékeuna à Hawkins.',
          },
        },
      ],
    },
    // Sphère Winterfell - s'active après avoir complété Jurassic Park
    {
      id: 'obj_portal_winterfell',
      name: 'Sphère de Winterfell',
      type: 'interactive',
      modelId: 'runic_sphere_portal',
      position: [15, 0, 15],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        portalTo: 'room_winterfell_reveil',
        isActive: false,
        activateAfterRoomCompleted: 'room_jurassic_reveil', // S'active après Jurassic
        portalColor: '#4169e1',
      },
      collider: { type: 'circle', size: [1.0] },
      onInteract: [
        {
          type: 'loadRoom',
          params: {
            roomId: 'room_winterfell_reveil',
            blockedMessage: 'Cette sphère s\'activera quand tu auras aidé Blue à Jurassic Park.',
          },
        },
      ],
    },
    // Sphère Terre du Milieu - s'active après avoir complété Winterfell
    {
      id: 'obj_portal_middleearth',
      name: 'Sphère de la Terre du Milieu',
      type: 'interactive',
      modelId: 'runic_sphere_portal',
      position: [10, 0, 18],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        portalTo: 'room_middle_earth_reveil',
        isActive: false,
        activateAfterRoomCompleted: 'room_winterfell_reveil', // S'active après Winterfell
        portalColor: '#ffd700',
      },
      collider: { type: 'circle', size: [1.0] },
      onInteract: [
        {
          type: 'loadRoom',
          params: {
            roomId: 'room_middle_earth_reveil',
            blockedMessage: 'Cette sphère s\'activera quand tu auras aidé Tyrion à Winterfell.',
          },
        },
      ],
    },
    // Pilier de cristal décoratif près de chaque portail
    {
      id: 'obj_pillar_wonderland',
      name: 'Pilier de Cristal',
      type: 'static',
      modelId: 'pillar',
      position: [4, 0, 4],
      rotation: [0, 0, 0],
      scale: [0.5, 2, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#ff69b4' },
    },
    {
      id: 'obj_pillar_hawkins',
      name: 'Pilier de Cristal',
      type: 'static',
      modelId: 'pillar',
      position: [16, 0, 4],
      rotation: [0, 0, 0],
      scale: [0.5, 2, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#ff0000' },
    },
    {
      id: 'obj_pillar_jurassic',
      name: 'Pilier de Cristal',
      type: 'static',
      modelId: 'pillar',
      position: [4, 0, 16],
      rotation: [0, 0, 0],
      scale: [0.5, 2, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#228b22' },
    },
    {
      id: 'obj_pillar_winterfell',
      name: 'Pilier de Cristal',
      type: 'static',
      modelId: 'pillar',
      position: [16, 0, 16],
      rotation: [0, 0, 0],
      scale: [0.5, 2, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#4169e1' },
    },
    {
      id: 'obj_pillar_middleearth',
      name: 'Pilier de Cristal',
      type: 'static',
      modelId: 'pillar',
      position: [10, 0, 19],
      rotation: [0, 0, 0],
      scale: [0.5, 2, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true, color: '#ffd700' },
    },
    // Cristaux décoratifs flottants
    {
      id: 'obj_crystal_1',
      name: 'Cristal Flottant',
      type: 'static',
      modelId: 'crystal',
      position: [3, 2, 10],
      rotation: [0.2, 0, 0.1],
      scale: [0.5, 1, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { floats: true, glows: true },
    },
    {
      id: 'obj_crystal_2',
      name: 'Cristal Flottant',
      type: 'static',
      modelId: 'crystal',
      position: [17, 2.5, 10],
      rotation: [-0.1, 0.5, 0.2],
      scale: [0.4, 0.8, 0.4],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { floats: true, glows: true },
    },
    {
      id: 'obj_crystal_3',
      name: 'Cristal Flottant',
      type: 'static',
      modelId: 'crystal',
      position: [10, 3, 3],
      rotation: [0.3, 0.2, -0.1],
      scale: [0.6, 1.2, 0.6],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { floats: true, glows: true },
    },
    // Petites étoiles décoratives (en spectral) - petits orbes flottants
    {
      id: 'obj_star_1',
      name: 'Étoile',
      type: 'static',
      modelId: 'orb',
      position: [4, 4, 4],
      rotation: [0, 0, 0],
      scale: [0.2, 0.2, 0.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#ffffff' },
    },
    {
      id: 'obj_star_2',
      name: 'Étoile',
      type: 'static',
      modelId: 'orb',
      position: [16, 5, 6],
      rotation: [0, 0, 0],
      scale: [0.15, 0.15, 0.15],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#ffffcc' },
    },
    {
      id: 'obj_star_3',
      name: 'Étoile',
      type: 'static',
      modelId: 'orb',
      position: [8, 4.5, 16],
      rotation: [0, 0, 0],
      scale: [0.18, 0.18, 0.18],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#ccccff' },
    },
    {
      id: 'obj_star_4',
      name: 'Étoile',
      type: 'static',
      modelId: 'orb',
      position: [14, 3.5, 14],
      rotation: [0, 0, 0],
      scale: [0.12, 0.12, 0.12],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#ffccff' },
    },

    // ============================================
    // DÉCOR ONIRIQUE SUPPLÉMENTAIRE
    // ============================================

    // Monde Réel - Fleurs lumineuses
    {
      id: 'obj_flower_1',
      name: 'Fleur Lumineuse',
      type: 'static',
      modelId: 'magic_flower',
      position: [2, 0, 8],
      rotation: [0, 0.5, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { glows: true, color: '#ff69b4' },
    },
    {
      id: 'obj_flower_2',
      name: 'Fleur Lumineuse',
      type: 'static',
      modelId: 'magic_flower',
      position: [18, 0, 12],
      rotation: [0, -0.3, 0],
      scale: [0.6, 0.6, 0.6],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { glows: true, color: '#87ceeb' },
    },
    {
      id: 'obj_flower_3',
      name: 'Fleur Lumineuse',
      type: 'static',
      modelId: 'magic_flower',
      position: [6, 0, 18],
      rotation: [0, 1.2, 0],
      scale: [0.7, 0.7, 0.7],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { glows: true, color: '#dda0dd' },
    },

    // Monde Réel - Arbres stylisés dans les coins
    {
      id: 'obj_tree_1',
      name: 'Arbre Ancien',
      type: 'static',
      modelId: 'tree',
      position: [1, 0, 1],
      rotation: [0, 0.3, 0],
      scale: [1.2, 1.5, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },
    {
      id: 'obj_tree_2',
      name: 'Arbre Ancien',
      type: 'static',
      modelId: 'tree',
      position: [19, 0, 1],
      rotation: [0, -0.5, 0],
      scale: [1, 1.3, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },
    {
      id: 'obj_tree_3',
      name: 'Arbre Mystique',
      type: 'static',
      modelId: 'tree',
      position: [1, 0, 19],
      rotation: [0, 0.8, 0],
      scale: [1.1, 1.4, 1.1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // Monde Spectral - Orbes mystiques supplémentaires
    {
      id: 'obj_orb_spectral_1',
      name: 'Orbe Mystique',
      type: 'static',
      modelId: 'orb',
      position: [2, 1.5, 5],
      rotation: [0, 0, 0],
      scale: [0.4, 0.4, 0.4],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#9370db', floats: true },
    },
    {
      id: 'obj_orb_spectral_2',
      name: 'Orbe Mystique',
      type: 'static',
      modelId: 'orb',
      position: [18, 2, 15],
      rotation: [0, 0, 0],
      scale: [0.35, 0.35, 0.35],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#da70d6', floats: true },
    },
    {
      id: 'obj_orb_spectral_3',
      name: 'Orbe Mystique',
      type: 'static',
      modelId: 'orb',
      position: [5, 2.5, 17],
      rotation: [0, 0, 0],
      scale: [0.3, 0.3, 0.3],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#00ced1', floats: true },
    },

    // Monde Spectral - Cristaux au sol lumineux
    {
      id: 'obj_ground_crystal_1',
      name: 'Cristal',
      type: 'static',
      modelId: 'crystal',
      position: [8, 0, 7],
      rotation: [0, 0.7, 0],
      scale: [0.3, 0.6, 0.3],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#e066ff' },
    },
    {
      id: 'obj_ground_crystal_2',
      name: 'Cristal',
      type: 'static',
      modelId: 'crystal',
      position: [12, 0, 13],
      rotation: [0, -0.4, 0],
      scale: [0.25, 0.5, 0.25],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#00fa9a' },
    },
    {
      id: 'obj_ground_crystal_3',
      name: 'Cristal',
      type: 'static',
      modelId: 'crystal',
      position: [15, 0, 8],
      rotation: [0, 1.1, 0],
      scale: [0.35, 0.7, 0.35],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true, color: '#87cefa' },
    },

    // Plateformes décoratives flottantes (spectral)
    {
      id: 'obj_floating_platform_1',
      name: 'Île Flottante',
      type: 'static',
      modelId: 'platform',
      position: [3, 3, 3],
      rotation: [0, 0.2, 0],
      scale: [1, 0.3, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { floats: true },
    },
    {
      id: 'obj_floating_platform_2',
      name: 'Île Flottante',
      type: 'static',
      modelId: 'platform',
      position: [17, 4, 17],
      rotation: [0, -0.5, 0],
      scale: [0.8, 0.25, 0.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { floats: true },
    },

    // ============================================
    // ÉLÉMENTS ATMOSPHÉRIQUES
    // ============================================

    // Fontaine centrale magique (visible dans les deux mondes)
    {
      id: 'obj_fountain',
      name: 'Fontaine des Dimensions',
      type: 'static',
      modelId: 'fountain',
      position: [10, 0, 10],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { glows: true },
      collider: { type: 'circle', size: [1.5] },
    },

    // Runes lumineuses au sol (monde réel)
    {
      id: 'obj_rune_real_1',
      name: 'Cercle Runique',
      type: 'static',
      modelId: 'rune_circle',
      position: [4, 0, 10],
      rotation: [0, 0.3, 0],
      scale: [1.5, 1, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { glows: true },
    },
    {
      id: 'obj_rune_real_2',
      name: 'Cercle Runique',
      type: 'static',
      modelId: 'rune_circle',
      position: [16, 0, 10],
      rotation: [0, -0.5, 0],
      scale: [1.3, 1, 1.3],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { glows: true },
    },

    // Runes lumineuses au sol (monde spectral) - plus intenses
    {
      id: 'obj_rune_spectral_1',
      name: 'Cercle Mystique',
      type: 'static',
      modelId: 'rune_circle',
      position: [7, 0, 5],
      rotation: [0, 0.8, 0],
      scale: [1.8, 1, 1.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true },
    },
    {
      id: 'obj_rune_spectral_2',
      name: 'Cercle Mystique',
      type: 'static',
      modelId: 'rune_circle',
      position: [13, 0, 15],
      rotation: [0, -0.3, 0],
      scale: [1.6, 1, 1.6],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true },
    },

  ],
  puzzles: [
    // Tutoriel Real/Spectral
    {
      id: 'puzzle_tutorial_spectral',
      name: 'Tutoriel du Voile',
      type: 'observation',
      state: 'active',
      description: 'Apprends à passer entre le monde réel et le monde spectral.',
      hint: 'Appuie sur Espace ou swipe vers le haut pour changer de monde.',
      requiredWorld: 'spectral',
      linkedObjectIds: ['obj_stars_spectral'],
      solution: {
        type: 'state',
        expected: { worldVisited: 'spectral' },
      },
      onSolve: [
        {
          type: 'showMessage',
          params: { message: 'Tu maîtrises maintenant le passage entre les mondes !' },
        },
      ],
    },
  ],
  connections: [
    {
      targetRoomId: 'room_wonderland_reveil',
      position: [5, 0, 5],
      direction: 'south',
      locked: false,
    },
  ],
  npcs: [
    // Étoile filante (animal pettable du hub) - luciole magique
    {
      id: 'npc_shooting_star',
      type: 'firefly',
      name: 'Étoile Filante',
      position: [7, 0.8, 12],
      scale: 1.2,
      idleAnimation: 'fly',
      petAnimation: 'spin',
      petRadius: 4,
      petCooldown: 2500,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Papillon cosmique (visible en spectral)
    {
      id: 'npc_cosmic_butterfly',
      type: 'butterfly',
      name: 'Papillon Cosmique',
      position: [13, 0.6, 12],
      scale: 1.5,
      idleAnimation: 'fly',
      petAnimation: 'happy',
      petRadius: 4,
      petCooldown: 2500,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
    // Nuage moelleux vivant (lapin blanc doux)
    {
      id: 'npc_fluffy_cloud',
      type: 'rabbit',
      name: 'Nuage Moelleux',
      position: [10, 0, 14],
      scale: 1.5,
      idleAnimation: 'sit',
      petAnimation: 'nuzzle',
      petRadius: 4,
      petCooldown: 2500,
      isPettable: true,
      visibleInWorld: 'real',
    },
    // TODO: Ajouter le Chapelier Fou après avoir implémenté la visibilité conditionnelle
    // basée sur la complétion de la quête Oracle (riddle_oracle_welcome)
  ],
};
